<template>
  <div class="flex flex-col gap-4">
    <ProcedureCreateModal
      ref="procedureCreateModal"
      :business-system-id="businessSystemId"
      @created="handleProcedureCreated"
    />

    <Card>
      <CardHeader class="flex flex-row items-center justify-between">
        <CardTitle>Procedures</CardTitle>
        <Button size="sm" :disabled="businessSystemId <= 0" @click="openProcedureCreateModal">
          New Procedure
        </Button>
      </CardHeader>
      <CardContent>
        <p v-if="procedureStore.isLoading" class="text-sm text-muted-foreground">
          Loading procedures...
        </p>
        <p v-else-if="procedures.length === 0" class="text-sm text-muted-foreground">
          No procedures yet. Create your first procedure to get started.
        </p>
        <UmisisAnimatedList v-else :items="procedures" list-class="flex flex-col divide-y">
          <template #default="{ item: procedure }">
            <div class="flex items-center justify-between gap-4 py-3">
              <div class="min-w-0">
                <p class="truncate font-medium">
                  {{ procedure.title }}
                </p>
                <p v-if="resolveProcedureGoal(procedure)" class="truncate text-sm text-muted-foreground">
                  {{ resolveProcedureGoal(procedure) }}
                </p>
              </div>
              <Button size="sm" variant="outline" as-child>
                <router-link
                  :to="{
                    name: 'ProcedureShowPage',
                    params: { businessSystem: businessSystemId, procedure: procedure.id },
                  }"
                >
                  Open
                </router-link>
              </Button>
            </div>
          </template>
        </UmisisAnimatedList>
      </CardContent>
    </Card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UmisisAnimatedList from '../../../../../../../../resources/js/components/umisis/generic/UmisisAnimatedList.vue'
import ProcedureCreateModal from '../../../components/business-systems/ProcedureCreateModal.vue'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../../resources/js/components/ui/card/index.js'
import { useProcedureStore } from '../../../stores/procedures.js'

const route = useRoute()
const router = useRouter()
const procedureStore = useProcedureStore()
const procedureCreateModal = ref(null)
const businessSystemId = computed(() => Number(route.params.businessSystem ?? 0))
const procedures = computed(() => {
  return procedureStore.items.filter((procedure) => procedure?.status !== 'archived')
})

const openProcedureCreateModal = () => {
  procedureCreateModal.value?.openModal()
}

const resolveProcedureGoal = (procedure) => {
  if (typeof procedure?.cached?.goal === 'string' && procedure.cached.goal.trim() !== '') {
    return procedure.cached.goal
  }

  return ''
}

const loadProcedures = async () => {
  if (!Number.isFinite(businessSystemId.value) || businessSystemId.value <= 0) {
    return
  }

  await procedureStore.fetchProceduresForBusinessSystem(businessSystemId.value)
}

const handleProcedureCreated = async (procedure) => {
  await router.push({
    name: 'ProcedureEditPage',
    params: {
      businessSystem: businessSystemId.value,
      procedure: procedure.id,
    },
  })
}

onMounted(() => {
  loadProcedures()
})

watch(businessSystemId, () => {
  loadProcedures()
})
</script>
